import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { IonicModule, IonModal, PopoverController } from '@ionic/angular';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ConversationListComponent } from 'src/app/components/conversation-list/conversation-list.component';
import { CONVERSATION_STATUS, routes } from 'src/app/constants';
import { Conversation } from 'src/models';
import {
  CONVERSATION_LINK_TYPES,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_MAP,
  ConversationLinkType,
  ConversationLinkTypeUnidirectional,
} from 'src/models/conversation-links';
import { Project } from 'src/models/projects';
import { ConversationsService } from 'src/services/conversations.service';
import { GlobalCreateService } from 'src/services/global-create.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule, ConversationListComponent],
  selector: 'app-conversation-links-list',
  templateUrl: './conversation-links-list.component.html',
  styleUrls: ['./conversation-links-list.component.css'],
})
export class ConversationLinksListComponent implements OnInit {
  @Input() conversationId: string;
  @Input() conversationFriendlyId: string;
  @Input() projectId: string;
  @Input() disabled = false;

  @ViewChild('linkExistingModal') linkExistingModal: IonModal;

  @ViewChildren(ConversationListComponent) conversationListComponents: QueryList<ConversationListComponent>;

  isLoading = false;
  isSubmitting = false;

  conversationLinks: any[] = [];
  linkedConversationGroups: {
    [key in ConversationLinkTypeUnidirectional]?: Record<string, string>;
  } = {};

  linkedConversation?: Partial<Conversation>;
  linkType?: ConversationLinkTypeUnidirectional;

  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS;
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS;

  constructor(
    private conversationsService: ConversationsService,
    private applicationRef: ApplicationRef,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private msgSrvc: MessageService,
    private globalCreateSrvc: GlobalCreateService,
  ) {}

  async ngOnInit() {
    await this.refreshConversationListComponents();
  }

  getRouterPath() {
    return `/${routes.DASHBOARD}/${routes.INBOX}/all/conversations`;
  }

  async loadConversationLinks() {
    this.linkedConversationGroups = {};

    this.conversationLinks = await this.conversationsService.getConversationLinks(this.conversationId);

    // Group conversations by type
    this.conversationLinks.forEach((link) => {
      const [linkedConversationId, unidirectionalTypeIndex] =
        link.linkedConversationId === this.conversationId ? [link.conversationId, 1] : [link.linkedConversationId, 0];

      const unidirectionalType = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_MAP[link.type][unidirectionalTypeIndex];

      this.linkedConversationGroups[unidirectionalType] = this.linkedConversationGroups[unidirectionalType] || {};
      this.linkedConversationGroups[unidirectionalType][linkedConversationId] = link.id;
    });
  }

  async refreshConversationListComponents() {
    this.isLoading = true;

    await this.loadConversationLinks();

    this.isLoading = false;

    this.applicationRef.tick();

    this.conversationListComponents.forEach((conversationListComponent) => {
      conversationListComponent.loadConversations();
    });
  }

  linkedConversationGroupEntries() {
    return Object.entries(this.linkedConversationGroups);
  }

  async showCreateOptions() {
    const optionValues = Array.from(Object.values(CONVERSATION_LINK_TYPES_UNIDIRECTIONAL));

    const options: ActionDropdownType[] = optionValues.map((linkType) => ({
      label: `New issue ${CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[linkType].toLowerCase()} this conversation`,
      id: linkType,
      icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[linkType],
    }));

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();
          this.linkType = optionValues[idx];
          this.globalCreateSrvc.conversation({
            linkType: optionValues[idx],
            linkConversationId: this.conversationId,
            linkConversationFriendlyId: this.conversationFriendlyId,
            project: {
              id: this.projectId,
            },
            status: CONVERSATION_STATUS.OPEN,
            saveCallback: async (conversation: Partial<Conversation>) => {
              await this.createLinkageApiCall(conversation.id, this.conversationId);
            },
          });
        },
      },
    });
    popover.present();
  }

  async openLinkExistingModal() {
    this.linkedConversation = undefined;
    this.linkType = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.RELATED_TO;

    await this.linkExistingModal.present();
  }

  async showLinkableConversations() {
    this.searchablePopoverService.conversations({
      event,
      where: [
        {
          field: 'id',
          operator: '!=',
          value: this.conversationId,
        },
      ],
      callback: async (id: string, _name: string, conversation: Partial<Conversation>) => {
        if (id) {
          this.linkedConversation = conversation;
        }
      },
    });
  }

  async showLinkTypeOptions() {
    const optionValues = Array.from(Object.values(CONVERSATION_LINK_TYPES_UNIDIRECTIONAL));

    const options: ActionDropdownType[] = optionValues.map((linkType) => ({
      label: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[linkType],
      id: linkType,
      icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[linkType],
    }));

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.linkType = optionValues[idx];
        },
      },
    });
    popover.present();
  }

  formatConversationTitle(conversation: Partial<Conversation>) {
    return `[${conversation.friendlyId}] ${conversation.title}`;
  }

  async submit() {
    this.isSubmitting = true;

    try {
      if (!this.linkedConversation) {
        this.msgSrvc.show('Please select a conversation to link');

        return;
      }

      let conversationId = this.conversationId;
      let linkedConversationId = this.linkedConversation.id;

      await this.createLinkageApiCall(conversationId, linkedConversationId);

      this.linkExistingModal.dismiss();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  async unlinkConversation(conversation: Partial<Conversation>, linkType: string) {
    const group = this.linkedConversationGroups[linkType as ConversationLinkTypeUnidirectional];
    const conversationLinkId = Object.entries(group).find(
      ([conversationId]) => conversationId === conversation.id,
    )?.[1];

    if (!conversationLinkId) {
      return;
    }

    await this.conversationsService.deleteConversationLink(this.conversationId, conversationLinkId);
    await this.msgSrvc.show('✔︎ Unlinked conversation');

    this.refreshConversationListComponents();
  }

  getConversationIdsFromGroup(group: Record<string, string>) {
    return Object.keys(group);
  }

  async createLinkageApiCall(conversationId: string, linkedConversationId: string) {
    let linkType: ConversationLinkType = CONVERSATION_LINK_TYPES.RELATED_TO;

    if (this.linkType === CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKS) {
      linkType = CONVERSATION_LINK_TYPES.BLOCKS;
    } else if (this.linkType === CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.BLOCKED_BY) {
      linkType = CONVERSATION_LINK_TYPES.BLOCKS;
      conversationId = this.linkedConversation.id;
      linkedConversationId = this.conversationId;
    } else if (this.linkType === CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKS) {
      linkType = CONVERSATION_LINK_TYPES.TRACKS;
    } else if (this.linkType === CONVERSATION_LINK_TYPES_UNIDIRECTIONAL.TRACKED_BY) {
      linkType = CONVERSATION_LINK_TYPES.TRACKS;
      conversationId = this.linkedConversation.id;
      linkedConversationId = this.conversationId;
    }

    await this.conversationsService.createConversationLink(conversationId, linkedConversationId, linkType);
    await this.msgSrvc.show('✔︎ Linked conversation');
    this.refreshConversationListComponents();
  }
}
